import { Component, Inject, OnInit, signal, PLATFORM_ID, OnDestroy, inject, computed } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, switchMap, of } from 'rxjs';
import {
  heroMagnifyingGlass,
  heroXMark,
  heroUserPlus,
  heroUsers,
  heroCheckCircle,
  heroUser,
  heroBriefcase,
  heroEnvelope,
  heroMapPin,
  heroCalendarDays,
  heroCheck,
  heroShieldCheck,
  heroStar,
  heroClock,
  heroHandRaised,
  heroLink,
  heroXCircle
} from '@ng-icons/heroicons/outline';
import { UsersApiService } from '../../../api/users.service';
import { UserAvatarComponent } from "../../../shared/user-avatar/user-avatar.component";
import { environment } from '../../../../environments/environment';
import { UserRequestsService } from '../services/user-requests.service';
import { SocketService } from '../../../core/services/socket.service';

interface SafetyKnightsUser {
  _id: string;
  followers: string[];
  following: string[];
  admin: string;
  creator: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  position: string;
  location: string;
  userbio?: string;
  profilePictureKey: string;
  pictureTransform: number;
  date: { $date: string };
  lastLoginDate?: { $date: string };
  isOnline?: boolean;
  isVerified?: boolean;
  tags?: string[];
  messageCount?: number;
  lastSeen?: string;

  // Updated connection status properties
  connectionRequestSent?: boolean;
  connectionRequestReceived?: boolean;
  connectionRequestId?: string | null;
  connectionStatus?: 'none' | 'pending_sent' | 'pending_received' | 'connected' | 'rejected_sent' | 'rejected_received';
  actualStatus?: 'pending' | 'accepted' | 'rejected' | null;
  canSendRequest?: boolean;
  requestDirection?: 'sent' | 'received' | null;
}

@Component({
  selector: 'app-user-selection-modal',
  standalone: true,
  imports: [
    CommonModule,
    NgIcon,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSnackBarModule,
    UserAvatarComponent
  ],
  providers: [
    provideIcons({
      heroMagnifyingGlass,
      heroXMark,
      heroUserPlus,
      heroUsers,
      heroCheckCircle,
      heroUser,
      heroBriefcase,
      heroEnvelope,
      heroMapPin,
      heroCalendarDays,
      heroCheck,
      heroShieldCheck,
      heroStar,
      heroClock,
      heroHandRaised,
      heroLink,
      heroXCircle,
    })
  ],
  templateUrl: './user-selection-modal.component.html',
  styleUrls: ['./user-selection-modal.component.scss']
})
export class UserSelectionModalComponent implements OnInit, OnDestroy {

  private userService = inject(UsersApiService);
  private userRequestsService = inject(UserRequestsService);
  private snackBar = inject(MatSnackBar);

  selectedUsers = signal<SafetyKnightsUser[]>([]);
  hasSelectedUsers = computed(() => this.selectedUser() !== null);
  searchQuery = signal('');
  selectedUser = signal<SafetyKnightsUser | null>(null);
  users = signal<SafetyKnightsUser[]>([]);
  isLoading = signal(false);
  isBrowser = signal(false);
  connectionMessage = signal('');

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  profileApiUrl = environment.profileApiUrl;

  constructor(
    public dialogRef: MatDialogRef<UserSelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    private socketService: SocketService,
  ) { }

  ngOnInit() {
    this.isBrowser.set(isPlatformBrowser(this.platformId));
    this.setupSearchSubscription();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubscription() {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap(query => {
          if (!query.trim()) {
            return of({ success: true, data: { users: [], count: 0 } });
          }

          this.isLoading.set(true);
          // Use the new backend endpoint that includes connection status
          return this.userService.getConnectionUserLists(query);
        })
      )
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          if (response.success && response.data.users) {
            // Backend now returns users with connection status included
            this.users.set(response.data.users);
          } else {
            this.users.set([]);
          }
        },
        error: (error) => {
          console.error('Search error:', error);
          this.users.set([]);
          this.isLoading.set(false);
        }
      });
  }

  onSearchChange(query: string) {
    this.searchQuery.set(query);
    this.searchSubject.next(query);
  }

  selectUser(user: SafetyKnightsUser) {
    const currentSelected = this.selectedUser();
    if (currentSelected?._id === user._id) {
      this.selectedUser.set(null);
      this.connectionMessage.set('');
    } else {
      this.selectedUser.set(user);
      this.connectionMessage.set('');
    }
  }

  isUserSelected(user: SafetyKnightsUser): boolean {
    return this.selectedUser()?._id === user._id;
  }

  sendConnectionRequest() {
  const selected = this.selectedUser();
  if (!selected) return;

  // Check for pending sent request
  if (selected.connectionStatus === 'pending_sent') {
    this.snackBar.open('Connection request already sent and is pending', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
    return;
  }

  // Check for pending received request
  if (selected.connectionStatus === 'pending_received') {
    this.snackBar.open('This user has already sent you a connection request. Check your requests.', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
    return;
  }

  // Check if already connected
  if (selected.connectionStatus === 'connected') {
    this.snackBar.open(`You are already connected with ${selected.firstName} ${selected.lastName}`, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
    return;
  }

  // Check if user previously declined their request
  if (selected.connectionStatus === 'rejected_received') {
    this.snackBar.open('You previously declined this user\'s request', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
    return;
  }

  // For rejected_sent status, allow retry with a warning message
  if (selected.connectionStatus === 'rejected_sent') {
    // Show a confirmation or warning but still allow sending
    this.snackBar.open('Your previous request was declined. Sending new request...', 'Close', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  this.isLoading.set(true);

  this.userRequestsService.sendConnectionRequest(
    selected._id,
    this.connectionMessage().trim() || undefined
  ).subscribe({
    next: (response) => {
      this.isLoading.set(false);
      if (response.success) {
        this.socketService.sendConnectionRequest({
          fromUserId: this.socketService.getCurrentUserId() || '',
          toUserId: selected._id,
          message: this.connectionMessage().trim() || undefined,
          requestId: response.request?._id || new Date().getTime().toString()
        });

        this.snackBar.open(
          `Connection request sent to ${selected.firstName} ${selected.lastName}`,
          'Close',
          {
            duration: 4000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          }
        );

        // Update the user in the list with new connection status
        this.users.update(users =>
          users.map(user =>
            user._id === selected._id
              ? {
                  ...user,
                  connectionStatus: 'pending_sent', // Updated to use new status
                  connectionRequestSent: true,
                  connectionRequestReceived: false,
                  connectionRequestId: response.request?._id,
                  actualStatus: 'pending',
                  canSendRequest: false,
                  requestDirection: 'sent'
                }
              : user
          )
        );

        this.selectedUser.set(null);
        this.connectionMessage.set('');

      } else {
        this.snackBar.open(
          response.message || 'Failed to send connection request',
          'Close',
          {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          }
        );
      }
    },
    error: (error) => {
      this.isLoading.set(false);
      console.error('Error sending connection request:', error);
      
      // Handle specific error cases
      if (error.status === 400 && error.error?.message) {
        this.snackBar.open(
          error.error.message,
          'Close',
          {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          }
        );
      } else {
        this.snackBar.open(
          'Failed to send connection request',
          'Close',
          {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          }
        );
      }
    }
  });
}


  getConnectionStatusText(user: SafetyKnightsUser): string {
    switch (user.connectionStatus) {
      case 'pending_sent':
        return 'Request Sent';
      case 'pending_received':
        return 'Request Pending';
      case 'connected':
        return 'Connected';
      case 'rejected_sent':
        return 'Request Declined';
      case 'rejected_received':
        return 'You Declined';
      case 'none':
      default:
        return 'Send Request';
    }
  }

  getConnectionStatusIcon(user: SafetyKnightsUser): string {
    switch (user.connectionStatus) {
      case 'pending_sent':
        return 'heroClock';
      case 'pending_received':
        return 'heroHandRaised';
      case 'connected':
        return 'heroLink';
      case 'rejected_sent':
        return 'heroXCircle';
      case 'rejected_received':
        return 'heroXCircle';
      case 'none':
      default:
        return 'heroUserPlus';
    }
  }

  getConnectionStatusClasses(user: SafetyKnightsUser): string {
    const baseClasses = 'tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-xl tw-font-semibold tw-text-sm tw-transition-all tw-duration-200';

    switch (user.connectionStatus) {
      case 'pending_sent':
        return `${baseClasses} tw-bg-yellow-100 dark:tw-bg-yellow-900/20 tw-text-yellow-700 dark:tw-text-yellow-400 tw-border tw-border-yellow-300 dark:tw-border-yellow-600 tw-cursor-not-allowed tw-opacity-75`;
      case 'pending_received':
        return `${baseClasses} tw-bg-blue-100 dark:tw-bg-blue-900/20 tw-text-blue-700 dark:tw-text-blue-400 tw-border tw-border-blue-300 dark:tw-border-blue-600 hover:tw-bg-blue-200 dark:hover:tw-bg-blue-900/40 hover:tw-scale-105 tw-cursor-pointer`;
      case 'connected':
        return `${baseClasses} tw-bg-green-100 dark:tw-bg-green-900/20 tw-text-green-700 dark:tw-text-green-400 tw-border tw-border-green-300 dark:tw-border-green-600 tw-cursor-not-allowed tw-opacity-75`;
      case 'rejected_sent':
        return `${baseClasses} tw-bg-red-100 dark:tw-bg-red-900/20 tw-text-red-700 dark:tw-text-red-400 tw-border tw-border-red-300 dark:tw-border-red-600 hover:tw-bg-red-200 dark:hover:tw-bg-red-900/40 hover:tw-scale-105 tw-cursor-pointer`;
      case 'rejected_received':
        return `${baseClasses} tw-bg-gray-100 dark:tw-bg-gray-900/20 tw-text-gray-700 dark:tw-text-gray-400 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-cursor-not-allowed tw-opacity-75`;
      case 'none':
      default:
        return `${baseClasses} tw-bg-indigo-100 dark:tw-bg-indigo-900/20 tw-text-indigo-700 dark:tw-text-indigo-400 tw-border tw-border-indigo-300 dark:tw-border-indigo-600 hover:tw-bg-indigo-200 dark:hover:tw-bg-indigo-900/40 hover:tw-scale-105 tw-cursor-pointer`;
    }
  }

  canSendRequestToUser(user: SafetyKnightsUser): boolean {
    return user.canSendRequest === true;
  }

  getJoinDate(dateString: string): string {
    if (!this.isBrowser()) return '';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    } catch {
      return 'Unknown';
    }
  }

  getFollowerCount(followers: string[]): number {
    return followers?.length || 0;
  }

  getFollowingCount(following: string[]): number {
    return following?.length || 0;
  }

  closeModal() {
    this.dialogRef.close();
  }

  handleConnectionAction(user: SafetyKnightsUser) {
    if (user.connectionStatus === 'pending_sent') {
      this.snackBar.open('Connection request already sent and is pending', 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    if (user.connectionStatus === 'connected') {
      this.snackBar.open(`You are already connected with ${user.firstName}`, 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    if (user.connectionStatus === 'pending_received') {
      this.snackBar.open('This user sent you a connection request. Check your requests.', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    if (user.connectionStatus === 'rejected_sent') {
      this.selectUser(user);
      return;
    }

    if (user.connectionStatus === 'rejected_received') {
      this.snackBar.open('You previously declined this user\'s request', 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    if (this.canSendRequestToUser(user)) {
      this.selectUser(user);
    }
  }

  handleAvatarClick($event: Event) {
    $event.stopPropagation();
  }
}
